import { getTheme, mergeStyleSets } from '@fluentui/react';
import { IPActionButton, IPIconButton } from 'baseComponents/Button/IPButton';
import { IPMessageBar, IPMessageBarType } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPText } from 'baseComponents/Text/IPText';
import { AdaptiveCardRenderer, AdaptiveSubmit, JsonObject } from 'components/AdaptiveCard/AdaptiveCardRenderer';
import { questionsCardAction } from 'components/Copilot/AutomationDrafting/automationDraftingAction';
import { CopilotChartBlock } from 'components/Copilot/Chart/CopilotChartBlock';
import { QuestionCard } from 'components/Copilot/Questions/QuestionCard';
import HrefLink from 'components/Links/HrefLink';
import { leftArrowIcon, rightArrowIcon } from 'globalStyles';
import {
  ContentBlockKind,
  CopilotConversationContentBlock,
  CopilotDataTableContent,
  CopilotLibraryDocumentEditorAction,
  CopilotLibraryDocumentEditorRequest,
} from 'models/copilot/copilotConversation';
import {
  buildQuestionAnswersJson,
  parseQuestionAnswersJson,
  parseQuestionSetPayload,
  questionAnsweredInChatInput,
  questionAnswersInput,
  questionSetIdInput,
} from 'models/copilot/questionSet';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { loadXlsx } from 'utils/xlsx';
import { CopilotContextProposalCard } from './CopilotContextProposalCard';
import { CopilotFileBlock } from './CopilotFileBlock';
import { CopilotMarkdown } from './CopilotMarkdown';

// The server-built picker cards whose submitted toggles are a selection of entities (the annual-plan
// checklists and the control-audit task picker). Every other card owns its submit itself.
const entitySelectionActions = ['confirmChecklist', 'createControlAuditTasks'];

interface ICopilotContentBlockRendererProps {
  blocks?: CopilotConversationContentBlock[];
  // the Copilot session of the conversation, for the download of a File block
  sessionId?: number;
  markdownWithTableStyles?: boolean;
  isMostRecent?: boolean;
  // the question card the session waits on (AITaskSessionStatus.WaitingForAnswer): answerable wherever it sits
  pendingQuestionBlockId?: string;
  onAdaptiveInputsChanged?: (blockId: string | undefined, inputs: Record<string, string>) => void;
  onAdaptiveSubmit?: (block: CopilotConversationContentBlock, payload: AdaptiveSubmit<JsonObject>) => void;
  onEntitySelection?: (selection: number[]) => void;
  onApprovalDetails?: (block: CopilotConversationContentBlock, payload: AdaptiveSubmit<JsonObject>) => void;
  onOpenLibraryDocumentEditor?: (request: CopilotLibraryDocumentEditorRequest) => void | Promise<void>;
}

export const CopilotContentBlockRenderer = (props: ICopilotContentBlockRendererProps) => {
  const theme = getTheme();
  const { t } = useTranslation(['copilot']);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [openingLibraryEditorBlockKey, setOpeningLibraryEditorBlockKey] = useState<string | undefined>(undefined);

  const styles = mergeStyleSets({
    tableWrapper: {
      maxWidth: '100%',
      overflowX: 'auto',
      border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
      borderRadius: 4,
      background: theme.palette.white,
    },
    table: {
      borderCollapse: 'collapse',
      minWidth: 420,
      width: '100%',
      tableLayout: 'auto',
    },
    tableHeader: {
      borderBottom: `1px solid ${theme.palette.neutralTertiaryAlt}`,
      color: theme.palette.neutralPrimary,
      fontWeight: 600,
      padding: '7px 9px',
      textAlign: 'left',
      verticalAlign: 'top',
      whiteSpace: 'nowrap',
      background: theme.palette.neutralLighterAlt,
    },
    tableCell: {
      borderBottom: `1px solid ${theme.palette.neutralLight}`,
      color: theme.palette.neutralPrimary,
      padding: '7px 9px',
      textAlign: 'left',
      verticalAlign: 'top',
      wordBreak: 'break-word',
    },
    tableFooter: {
      color: theme.palette.neutralSecondary,
      paddingTop: 4,
    },
    blockSurface: {
      border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
      borderRadius: 4,
      background: theme.palette.white,
      padding: 10,
    },
    blockTitle: {
      color: theme.palette.neutralPrimary,
      fontWeight: 600,
    },
    blockMeta: {
      color: theme.palette.neutralSecondary,
      wordBreak: 'break-word',
    },
    payloadPreview: {
      background: theme.palette.neutralLighterAlt,
      border: `1px solid ${theme.palette.neutralLight}`,
      borderRadius: 4,
      color: theme.palette.neutralPrimary,
      fontFamily: 'Consolas, monospace',
      fontSize: 12,
      margin: 0,
      maxHeight: 220,
      overflow: 'auto',
      padding: 8,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
    },
    switcherText: {
      color: theme.palette.neutralSecondary,
      minWidth: 42,
      textAlign: 'center',
    },
  });

  const exportDataTable = async (dataTable: CopilotDataTableContent) => {
    setIsExporting(true);
    try {
      const xlsx = await loadXlsx();
      const headers = dataTable.columns.map((col) => col.name || col.key);
      const rows = dataTable.rows.map((row) =>
        dataTable.columns.map((col) => {
          const value = row[col.key];
          if (value === null || value === undefined) return '';
          if (typeof value === 'boolean') return value ? 'true' : 'false';
          if (typeof value === 'number') return String(value);

          return typeof value === 'string' ? value : JSON.stringify(value);
        }),
      );
      const ws = xlsx.utils.aoa_to_sheet([headers, ...rows]);
      const wb = xlsx.utils.book_new();
      xlsx.utils.book_append_sheet(wb, ws, 'Data');
      xlsx.writeFile(wb, 'export.xlsx');
    } finally {
      setIsExporting(false);
    }
  };

  const renderMarkdown = (content?: string, withTableStyles: boolean = false) => {
    return <CopilotMarkdown content={content} withTableStyles={withTableStyles} />;
  };

  const renderValue = (value: unknown): JSX.Element | string | null => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    if (typeof value === 'number') return value.toLocaleString();

    const text = typeof value === 'string' ? value : JSON.stringify(value);
    const markdownLink = text.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (markdownLink) {
      return <HrefLink href={markdownLink[2]}>{markdownLink[1]}</HrefLink>;
    }

    const lines = text.split(/<br\s*\/?>(?:\s*)/i);
    if (lines.length <= 1) return text;

    return (
      <>
        {lines.map((line, index) => (
          <span key={`${line}-${index}`}>
            {line}
            {index < lines.length - 1 && <br />}
          </span>
        ))}
      </>
    );
  };

  const renderDataTable = (dataTable?: CopilotDataTableContent) => {
    if (!dataTable || dataTable.columns.length === 0) return null;
    const shown = dataTable.returnedCount > 0 ? dataTable.returnedCount : dataTable.rows.length;
    const total = dataTable.totalCount > 0 ? dataTable.totalCount : shown;
    const maxReturnedRows = dataTable.maxReturnedRows > 0 ? dataTable.maxReturnedRows : shown;

    return (
      <IPStack tokens={{ childrenGap: 4 }}>
        <IPStack.Item>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  {dataTable.columns.map((column) => (
                    <th key={column.key} className={styles.tableHeader}>
                      {column.name || column.key}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dataTable.rows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {dataTable.columns.map((column) => (
                      <td key={column.key} className={styles.tableCell}>
                        {renderValue(row[column.key])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </IPStack.Item>
        {dataTable.isTruncated ? (
          <IPStack.Item className={styles.tableFooter}>
            <IPText variant="small">
              {t('copilot:Chat.DataTable.Truncated', { shown, total, max: maxReturnedRows })}
            </IPText>
          </IPStack.Item>
        ) : total > shown ? (
          <IPStack.Item className={styles.tableFooter}>
            <IPText variant="small">{t('copilot:Chat.DataTable.Showing', { shown, total })}</IPText>
          </IPStack.Item>
        ) : null}
      </IPStack>
    );
  };

  const getPayloadObject = (payload: unknown): Record<string, unknown> | undefined => {
    return payload && typeof payload === 'object' && !Array.isArray(payload)
      ? (payload as Record<string, unknown>)
      : undefined;
  };

  const applyAdaptiveValues = (cardJson: string, values?: Record<string, string>): string => {
    if (!cardJson || !values) return cardJson;

    try {
      const card = JSON.parse(cardJson);

      const applyValuesToElements = (elements: unknown) => {
        if (!Array.isArray(elements)) return;

        for (const element of elements) {
          if (!element || typeof element !== 'object') continue;

          const item = element as {
            id?: string;
            value?: string;
            items?: unknown;
            columns?: unknown;
            body?: unknown;
            actions?: unknown;
            // an Action.ShowCard's card: its inputs are the card's too (the request card's e-mail part)
            card?: { body?: unknown; actions?: unknown };
          };

          if (item.id && values[item.id] !== undefined) {
            item.value = values[item.id];
          }

          applyValuesToElements(item.items);
          applyValuesToElements(item.columns);
          applyValuesToElements(item.body);
          applyValuesToElements(item.actions);
          applyValuesToElements(item.card?.body);
          applyValuesToElements(item.card?.actions);
        }
      };

      applyValuesToElements(card?.body);
      applyValuesToElements(card?.actions);

      return JSON.stringify(card);
    } catch {
      return cardJson;
    }
  };

  const getAdaptiveCard = (block: CopilotConversationContentBlock): string | JsonObject | undefined => {
    if (block.adaptiveCardJson) return applyAdaptiveValues(block.adaptiveCardJson, block.adaptiveInputs);

    const payload = getPayloadObject(block.payload);
    if (!payload) return undefined;

    return applyAdaptiveValues(JSON.stringify(payload), block.adaptiveInputs);
  };

  const parseCardObject = (card: string | JsonObject): Record<string, unknown> | undefined => {
    if (typeof card !== 'string') return card;

    try {
      return getPayloadObject(JSON.parse(card));
    } catch {
      return undefined;
    }
  };

  const parseActionData = (data: unknown): Record<string, unknown> | undefined => {
    if (typeof data !== 'string') return getPayloadObject(data);

    try {
      return getPayloadObject(JSON.parse(data));
    } catch {
      return undefined;
    }
  };

  const isAITaskApprovalCard = (card: string | JsonObject): boolean => {
    const parsedCard = parseCardObject(card);
    const actions = parsedCard?.actions;
    if (!Array.isArray(actions)) return false;

    return actions.some((action) => {
      const actionObject = getPayloadObject(action);
      const data = parseActionData(actionObject?.data);

      return data?.action === 'aiTaskApproval';
    });
  };

  const handleAdaptiveInputChanged = (block: CopilotConversationContentBlock, inputs: Record<string, string>) => {
    props.onAdaptiveInputsChanged?.(block.id, inputs);
  };

  const readNumber = (value: unknown): number | undefined => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value !== 'string') return undefined;

    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : undefined;
  };

  const readString = (value: unknown): string | undefined => {
    return typeof value === 'string' && value.trim() ? value : undefined;
  };

  const tryGetLibraryDocumentEditorRequest = (
    block: CopilotConversationContentBlock,
    payload: AdaptiveSubmit<JsonObject>,
  ): CopilotLibraryDocumentEditorRequest | undefined => {
    if (payload.actionData.action !== CopilotLibraryDocumentEditorAction) return undefined;

    const libraryLinkId = readNumber(payload.actionData.libraryLinkId);
    if (libraryLinkId === undefined) return undefined;

    return {
      libraryLinkId,
      libraryListId: readNumber(payload.actionData.libraryListId),
      title: readString(payload.actionData.title),
      pendingInstruction: readString(payload.actionData.pendingInstruction),
      handoffBlockId: block.id,
    };
  };

  const getLibraryEditorBlockKey = (block: CopilotConversationContentBlock): string => {
    return block.id ?? `${block.kind}-${block.title ?? 'library-editor'}`;
  };

  const handleAdaptiveSubmit = async (block: CopilotConversationContentBlock, payload: AdaptiveSubmit<JsonObject>) => {
    const editorRequest = tryGetLibraryDocumentEditorRequest(block, payload);
    if (editorRequest) {
      const blockKey = getLibraryEditorBlockKey(block);
      setOpeningLibraryEditorBlockKey(blockKey);

      try {
        await props.onOpenLibraryDocumentEditor?.(editorRequest);
      } finally {
        setOpeningLibraryEditorBlockKey(undefined);
      }

      return;
    }

    if (payload.actionData.action === 'aiTaskApprovalDetails') {
      props.onApprovalDetails?.(block, payload);

      return;
    }

    if (payload.actionData.action === 'aiTaskApproval') {
      props.onAdaptiveSubmit?.(block, payload);

      return;
    }

    // Commit the submitted values on the rendered block itself, synchronously: the adaptive-card
    // library only raises input-changed events for values the user TOUCHED, so a submit with
    // untouched defaults would otherwise reach the runtime (onEntitySelection runs in this same
    // tick, before the queued state updates land) with a block that has no inputs at all.
    block.adaptiveInputs = { ...(block.adaptiveInputs ?? {}), ...payload.inputs };

    handleAdaptiveInputChanged(block, payload.inputs);
    props.onAdaptiveSubmit?.(block, payload);

    // Only the entity-picker cards feed the generic entity-selection path: their toggles ARE the
    // selection. Any other card carries its own action and is handled by the usage that built it
    // (through onAdaptiveSubmit above) - deriving a selection from those too would send an empty one,
    // which renders as a bogus "I have selected 0 items" question from the user.
    const submittedAction = typeof payload.actionData.action === 'string' ? payload.actionData.action : '';
    if (submittedAction.length > 0 && !entitySelectionActions.includes(submittedAction)) {
      return;
    }

    const selection = Object.entries(payload.inputs)
      .filter(([_key, value]) => value === 'true')
      .map(([key]) => {
        const [controlIdPart] = key.split('__');
        const parsed = Number(controlIdPart);

        return Number.isNaN(parsed) ? null : parsed;
      })
      .filter((id): id is number => id !== null);

    props.onEntitySelection?.(Array.from(new Set(selection)));
  };

  const renderAdaptiveCard = (block: CopilotConversationContentBlock) => {
    const card = getAdaptiveCard(block);
    if (!card) return renderMarkdown(block.text, props.markdownWithTableStyles);

    return (
      <AdaptiveCardRenderer
        card={card}
        // the card as sent, without the typed values written back into it: typing does not rebuild the card
        cardKey={`${block.id ?? ''}|${block.adaptiveCardJson ?? JSON.stringify(block.payload ?? null)}`}
        onSubmit={(payload) => void handleAdaptiveSubmit(block, payload)}
        onInputChanged={isAITaskApprovalCard(card) ? undefined : (inputs) => handleAdaptiveInputChanged(block, inputs)}
        disabled={!props.isMostRecent || openingLibraryEditorBlockKey === getLibraryEditorBlockKey(block)}
      />
    );
  };

  // The question card of an agent's typed questions (phase 32): the answers json goes onto the block's inputs and
  // through the same submit lane as an adaptive card, where the running process intercepts the action and executes.
  const renderQuestionCard = (block: CopilotConversationContentBlock) => {
    const questionSet = parseQuestionSetPayload(block.payload);
    if (!questionSet) return renderMarkdown(block.text, props.markdownWithTableStyles);

    const sent = parseQuestionAnswersJson(block.adaptiveInputs?.[questionAnswersInput]);
    const isPending = !!block.id && block.id === props.pendingQuestionBlockId;

    return (
      <QuestionCard
        questionSet={questionSet}
        answers={sent}
        canAnswer={(props.isMostRecent === true || isPending) && !sent}
        answeredInChat={block.adaptiveInputs?.[questionAnsweredInChatInput] === 'true'}
        onSubmit={(answers) => {
          const inputs: Record<string, string> = {
            [questionAnswersInput]: buildQuestionAnswersJson(questionSet.setId, answers),
            [questionSetIdInput]: questionSet.setId ?? '',
          };
          //the inputs land on the block only when the RUNNING process accepts the action (review fix WB2): a dead
          //process must not leave a block that claims the answers were sent -- the controller persists them on handled
          props.onAdaptiveSubmit?.(block, { inputs, actionData: { action: questionsCardAction } });
        }}
      />
    );
  };

  // A notice of the answer (the files a model produced that were left out): one line each, as the server wrote them.
  const renderNotice = (block: CopilotConversationContentBlock) => {
    const lines = (block.text ?? '').split('\n').filter((line) => line.trim() !== '');
    if (lines.length === 0) return null;

    return (
      <IPMessageBar messageBarType={IPMessageBarType.warning} isMultiline>
        {lines.map((line, index) => (
          <div key={index}>{line}</div>
        ))}
      </IPMessageBar>
    );
  };

  const renderBlock = (block: CopilotConversationContentBlock, index: number) => {
    switch (block.kind) {
      case ContentBlockKind.Markdown:
        return renderMarkdown(block.text, props.markdownWithTableStyles);
      case ContentBlockKind.DataTable:
        return renderDataTable(block.dataTable);
      case ContentBlockKind.Link:
        return block.link ? <HrefLink href={block.link}>{block.text || block.link}</HrefLink> : null;
      case ContentBlockKind.AdaptiveCard:
        return renderAdaptiveCard(block);
      case ContentBlockKind.QuestionSet:
        return renderQuestionCard(block);
      case ContentBlockKind.File:
        return <CopilotFileBlock block={block} sessionId={props.sessionId} />;
      case ContentBlockKind.Chart:
        return <CopilotChartBlock block={block} />;
      case ContentBlockKind.ContextProposal:
        return <CopilotContextProposalCard block={block} />;
      default:
        return null;
    }
  };

  const allBlocks = props.blocks?.filter((block) => block) ?? [];
  // A notice, a card that asks the user for something, a file card and a context proposal card are always in view,
  // never one of the pages the switcher pages through. Below the pages come the notices, then the cards, then the
  // files and proposals, the cards and the files each in the answer's order.
  const isInputBlock = (block: CopilotConversationContentBlock) =>
    block.kind === ContentBlockKind.QuestionSet || block.kind === ContentBlockKind.AdaptiveCard;
  const isFileBlock = (block: CopilotConversationContentBlock) =>
    block.kind === ContentBlockKind.File || block.kind === ContentBlockKind.ContextProposal;
  const notices = allBlocks.filter((block) => block.kind === ContentBlockKind.Notice);
  const inputBlocks = allBlocks.filter(isInputBlock);
  const fileBlocks = allBlocks.filter(isFileBlock);
  const blocks = allBlocks.filter(
    (block) => block.kind !== ContentBlockKind.Notice && !isInputBlock(block) && !isFileBlock(block),
  );
  const firstBlockKey = blocks[0]?.id ?? blocks[0]?.kind;

  useEffect(() => {
    setSelectedIndex(0);
  }, [blocks.length, firstBlockKey]);

  const renderNotices = () =>
    notices.map((notice, index) => (
      <IPStack.Item key={notice.id ?? `notice-${index}`}>{renderNotice(notice)}</IPStack.Item>
    ));

  const renderInputBlocks = () =>
    inputBlocks.map((block, index) => (
      <IPStack.Item key={block.id ?? `input-${block.kind}-${index}`}>{renderBlock(block, index)}</IPStack.Item>
    ));

  const renderFileBlocks = () =>
    fileBlocks.map((block, index) => (
      <IPStack.Item key={block.id ?? `file-${index}`}>{renderBlock(block, index)}</IPStack.Item>
    ));

  if (blocks.length === 0) {
    if (notices.length === 0 && inputBlocks.length === 0 && fileBlocks.length === 0) return null;

    return (
      <IPStack tokens={{ childrenGap: 8 }}>
        {renderNotices()}
        {renderInputBlocks()}
        {renderFileBlocks()}
      </IPStack>
    );
  }

  const currentIndex = Math.min(selectedIndex, blocks.length - 1);
  const currentBlock = blocks[currentIndex];
  const showSwitcher = blocks.length > 1;
  const isDataTable = currentBlock.kind === ContentBlockKind.DataTable && !!currentBlock.dataTable;
  const showHeader = showSwitcher || isDataTable;

  const goToPreviousBlock = () => {
    setSelectedIndex((index) => Math.max(index - 1, 0));
  };

  const goToNextBlock = () => {
    setSelectedIndex((index) => Math.min(index + 1, blocks.length - 1));
  };

  return (
    <IPStack tokens={{ childrenGap: 8 }}>
      {showHeader && (
        <IPStack.Item>
          <IPStack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
            {showSwitcher && (
              <>
                <IPStack.Item>
                  <IPIconButton
                    iconProps={leftArrowIcon}
                    title={t('copilot:Chat.ContentBlockSwitcher.Previous')}
                    disabled={currentIndex === 0}
                    onClick={goToPreviousBlock}
                    styles={{ root: { height: 24, width: 24 }, icon: { fontSize: 12 } }}
                  />
                </IPStack.Item>
                <IPStack.Item className={styles.switcherText}>
                  <IPText>{`${currentIndex + 1} / ${blocks.length}`}</IPText>
                </IPStack.Item>
                <IPStack.Item>
                  <IPIconButton
                    iconProps={rightArrowIcon}
                    title={t('copilot:Chat.ContentBlockSwitcher.Next')}
                    disabled={currentIndex === blocks.length - 1}
                    onClick={goToNextBlock}
                    styles={{ root: { height: 24, width: 24 }, icon: { fontSize: 12 } }}
                  />
                </IPStack.Item>
              </>
            )}
            {isDataTable && (
              <IPStack.Item>
                <IPActionButton
                  iconProps={{ iconName: 'Download' }}
                  text={t('copilot:Chat.DataTable.Export')}
                  disabled={isExporting}
                  onClick={() => exportDataTable(currentBlock.dataTable!)}
                />
              </IPStack.Item>
            )}
          </IPStack>
        </IPStack.Item>
      )}
      <IPStack.Item key={currentBlock.id ?? `${currentBlock.kind}-${currentIndex}`}>
        {renderBlock(currentBlock, currentIndex)}
      </IPStack.Item>
      {renderNotices()}
      {renderInputBlocks()}
      {renderFileBlocks()}
    </IPStack>
  );
};
